import { useCallback, useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'

import { Box, Select } from '@enechain/ecloud-designsystem'
import type { SelectOption } from '@enechain/ecloud-designsystem'
import { FaXmarkRegular as CloseIcon } from '@enechain/ecloud-icons'

import { selectFontFix } from '~/components/common/filters/selectFontFix'
import { iconSize } from '~/constants/iconSize'

export type DsMultiSelectOption = {
  value: string
  label: string
}

type Props = {
  options: DsMultiSelectOption[]
  /** 選択中の value 集合。 空 = 全件 (絞り込みなし)。 */
  selected: Set<string>
  /** 新しい集合を丸ごと渡す (= 親が state を完全に持つ)。 */
  onChange: (next: Set<string>) => void
  'aria-label': string
  /** 未選択時に control へ出す文言。 */
  placeholder?: string
  width?: string
  minWidth?: string
  /**
   * 入力不備の表示 (DS `Select` の赤枠 + `aria-invalid`)。
   * 単一 select 側 (`DsSelect`) と同じ口を multi にも開ける。
   */
  invalid?: boolean
  /** 候補が 0 件の時に menu へ出す文言。 未指定は `common:form.noOptions`。 */
  noOptionsMessage?: string
  /**
   * 入力中の文字列に一致する候補が無い時の文言。 未指定は `noOptionsMessage`、
   * それも無ければ `common:form.noMatch`。
   */
  noMatchMessage?: string
  /** 入力欄の id (外側の `<label htmlFor>` と結ぶ)。 */
  inputId?: string
}

/**
 * menu を開いている間の Escape を、 親の DS `Modal` (Zag Dialog) より先に受けて
 * menu だけを閉じる。
 *
 * Zag Dialog は Escape を **document の capture 相**で受けて Modal を閉じるため、
 * react-select の input に keydown が届く前に Modal ごと閉じてしまう
 * (`DsMultiSelect` を Modal に置いた probe で再現)。 `useDropdownDismiss` と同じく
 * document より外側の **window の capture 相**で止め、 menu は制御側で閉じる。
 */
const useEscapeBeforeDialog = (
  isMenuOpen: boolean,
  onClose: () => void,
): void => {
  useEffect(() => {
    if (!isMenuOpen) return
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return
      e.stopPropagation()
      e.preventDefault()
      onClose()
    }
    window.addEventListener('keydown', handleKeyDown, true)
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true)
    }
  }, [isMenuOpen, onClose])
}

/**
 * 選択済みチップの削除ボタン。
 *
 * react-select の既定 `MultiValueRemove` は aria-label を
 * `"Remove " + children` で作るが、 DS が `MultiValueLabel` を差し替えている
 * 影響で children が解決されず **「Remove undefined」** になる
 * (codex/devin review 指摘 #6774)。 選択中は「どの承認者を外すのか」 が
 * screen reader に伝わらないので、 `data.label` から日本語の名前を作る。
 *
 * react-select を直接 import しない (本 app の package.json に無い
 * transitive dep) ため、 props は必要な形だけを構造的に受ける。
 */
type MultiValueRemoveProps = {
  data: { label: string }
  innerProps: React.HTMLAttributes<HTMLDivElement>
}

const MultiValueRemove = ({
  data,
  innerProps,
}: MultiValueRemoveProps): React.ReactElement => {
  const { t } = useTranslation()
  return (
    <div
      {...innerProps}
      aria-label={t('common:multiSelect.removeAriaLabel', {
        label: data.label,
      })}
      role="button"
      tabIndex={-1}
    >
      <CloseIcon boxSize={iconSize.badgeInline} />
    </div>
  )
}

/**
 * 複数選択の共通部品。 **DS `Select` の `isMulti`** を使う。
 *
 * ## なぜ自前 dropdown ではなく DS `Select` か
 *
 * 単一 select と同じく、 複数選択も**正は DS `Select`**。 DS が multi 用に別 component を
 * 持っているわけではなく、 `Select` の `isMulti` が multi の口
 * (`SelectProps` は `isMulti` で `onChange` の型が分岐する discriminated union で、
 * DS の story / VRT も `MultiSelect*` として multi の全 state を持つ)。
 *
 * 既存の `FilterMultiSelect` / `MultiSelect` は「Chakra v3 の `Menu` / `Combobox`
 * namespace primitive の型が react-19 前提で本 app (react-18) と噛み合わない」 ことを
 * 理由に自前 dropdown で作られている。 **この理由は DS `Select` には当たらない** —
 * DS `Select` は Chakra の namespace primitive ではなく `chakra-react-select` 実装で、
 * react-18 でそのまま型が通る (2026-08-24 実測: `Select<Option, true>` の probe が
 * tsc エラー 0)。
 *
 * option の見た目は DS のまま (装飾を足さない)。 2026-08-24 デザイナー判断で、
 * 一度入れた option 左の色 dot は**外した** (凡例が list に出るのは冗長)。
 *
 * ## menu の portal 先
 *
 * `DsSelect` と同じ理由 (DS `Modal` が body に `pointer-events: none` を付ける) で
 * body へ portal しつつ `pointerEvents: 'auto'` を戻す。
 */
export const DsMultiSelect = ({
  options,
  selected,
  onChange,
  'aria-label': ariaLabel,
  placeholder,
  width,
  minWidth,
  invalid,
  noOptionsMessage,
  noMatchMessage,
  inputId,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const closeMenu = useCallback(() => {
    setIsMenuOpen(false)
  }, [])
  useEscapeBeforeDialog(isMenuOpen, closeMenu)
  const optionValues = new Set(options.map((o) => o.value))
  const selectedOptions = [
    ...options.filter((o) => selected.has(o.value)),
    // 候補から外れた選択値 (失効 BG など) も chip を残して外せるようにする。
    ...[...selected]
      .filter((v) => !optionValues.has(v))
      .map((v) => ({ value: v, label: v })),
  ]
  return (
    <Box width={width} minWidth={minWidth} css={selectFontFix}>
      <Select<SelectOption, true>
        aria-label={ariaLabel}
        /* 1 件選ぶたびに候補リストが閉じないようにする (2026-09-07 デザイナー指摘)。

         react-select の既定は `closeMenuOnSelect: true` で、 `setValue` が毎回
         `onMenuClose()` を呼ぶ。 複数選択なのに 1 件ごとに閉じると、 承認者を数人
         指定するだけで開き直しを繰り返すことになる。 本 app の他の複数選択
         (`FilterMultiSelect` / `MultiSelect`) はどちらも選択では閉じず、 プロトの
         共通ヘルパ `design/js/ecl-multi-select.js` も閉じない (option click は
         selection を足すだけで `box.open` を触らない)。 「開いたまま」 が本来の
         挙動で、 DS 経由のここだけが例外だった。

         `blurInputOnSelect` も併せて落とす必要がある。 これは単なる保険ではない:
         react-select の既定値は `isTouchCapable()` で、 `selectOption` の末尾は
         **`closeMenuOnSelect` と無関係に** `blurInput()` を呼ぶ。 その blur を
         `onInputBlur` が拾って `onMenuClose()` するため、 タッチ対応ブラウザ
         (タッチスクリーンの Windows 機、 devtools のデバイスエミュレーション、
         タブレット) では `closeMenuOnSelect={false}` だけでは閉じたままになる。
         テスト環境の happy-dom も `document.createEvent('TouchEvent')` が
         throw しないため touch-capable 判定になり、 これが無いと本挙動の回帰
         テストが書けない。

         `hideSelectedOptions` は **既定 (isMulti なので true) のまま**にする。
         選んだ人が候補から消えるのはプロトと同じ挙動で
         (`ecl-multi-select.js` の `renderMenu` が選択済みを `return` で飛ばし、
         `setSelected` が「選択で候補が減る…ため高さを取り直す」 と書いている)、
         `false` にするとプロトから離れる。 */
        blurInputOnSelect={false}
        closeMenuOnSelect={false}
        components={{ MultiValueRemove }}
        inputId={inputId}
        invalid={invalid}
        // portal しても **下方向固定のままだと viewport を突き抜ける**
        // (`DsSelect` と同じ理由)。 空きが足りない時は上へ出す。
        isMulti
        menuIsOpen={isMenuOpen}
        menuPlacement="auto"
        menuPortalTarget={document.body}
        noOptionsMessage={({ inputValue }) =>
          inputValue.trim() !== ''
            ? (noMatchMessage ?? noOptionsMessage ?? t('common:form.noMatch'))
            : (noOptionsMessage ?? t('common:form.noOptions'))
        }
        onChange={(next) => {
          onChange(new Set(next.map((o) => o.value)))
        }}
        onMenuClose={closeMenu}
        onMenuOpen={() => {
          setIsMenuOpen(true)
        }}
        options={options}
        placeholder={placeholder}
        selectedOptions={selectedOptions}
        // [DS差異] UI 辞書 components/select.md「`--sm` も同じ寸法」。 DS の sm (34px / 12px) は
        // 同じ行の入力欄・ボタン (40px) と高さがずれるため、 単一選択の `DsSelect` と同じ md で描く
        size="md"
        styles={{
          menuPortal: (base) => ({
            ...base,
            zIndex: 1500,
            pointerEvents: 'auto',
          }),
        }}
        /* menu を開いたままにすると、 react-select の既定 `tabSelectsValue: true` が
         牙を剥く。 これまでは 1 件選んだ時点で menu が閉じていたので Tab は
         素直に次の要素へ抜けたが、 開いたままだと Tab が **フォーカス中の候補を
         選択してしまう**。 キーボード利用者が「選び終わったので次へ」 と Tab を
         押すと、 意図しない相手が 1 人足される (先に Escape を押す必要がある)。
         menu を開いたままにする変更が新たに作る事故なので、 セットで塞ぐ。 */
        tabSelectsValue={false}
      />
    </Box>
  )
}
